/**
 * articleCategories.ts
 * ────────────────────────────────────────────────────────────────────────────
 * Category definitions and weighted scoring engine for article classification.
 *
 * Scoring tiers:
 *   boost   → exact phrase match → +5 pts  (definitive, unmistakable signals)
 *   phrases → multi-word match   → +3 pts  (contextual, strong signals)
 *   words   → whole-word match   → +1 pt   (supporting signals, word-boundary safe)
 *
 * IMPORTANT: `words` entries use \b word-boundary regex so "tech" won't hit
 * "toxic", "secular" won't falsely trigger on "secular workplace" as apologetics, etc.
 *
 * Category winner = highest scoring category.
 * A secondary category badge is shown if it scores ≥ 3 pts AND ≥ 60% of winner.
 */

export interface CategoryDef {
  id: string;
  labelEn: string;
  labelEs: string;
  color: string;
  /** Exact phrase matches — 5 pts each. Definitive, high-confidence signals. */
  boost: string[];
  /** Multi-word phrase matches — 3 pts each. Contextual topic phrases. */
  phrases: string[];
  /**
   * Whole-word keyword matches — 1 pt each.
   * These use regex word-boundary matching (\b) so "tech" won't match "toxic".
   */
  words: string[];
}

export const CATEGORIES: CategoryDef[] = [
  // ── Apologetics ────────────────────────────────────────────────────────────
  {
    id: 'apologetics',
    labelEn: 'Apologetics',
    labelEs: 'Apologética',
    color: '#3B82F6',
    boost: [
      // Direct naming
      'apologetics', 'apologetic', 'apologist', 'apologética', 'apologetica',
      'defend the faith', 'defense of the faith', 'defending the faith',
      'christian apologetics', 'apologética cristiana',
      // Specific article titles that are clearly apologetics
      'why purgatory is not biblical', 'purgatory is not biblical',
      'disproving the quran', 'quran vs the bible', 'quran vs bible',
      'islam and christianity', 'does the quran', 'refuting the quran',
    ],
    phrases: [
      // Bible reliability
      'can you trust the bible', 'is the bible reliable', 'bible reliable',
      'is the new testament reliable', 'new testament reliable',
      'reliability of scripture', 'manuscript evidence',
      'dead sea scrolls', 'archaeological evidence for the bible',
      // God's existence
      'evidence for god', 'evidence for christianity', 'evidence for christ',
      'existence of god', 'god exists', 'arguments for god',
      'does god exist', 'why believe in god', 'reason to believe in god',
      'cosmological argument', 'teleological argument', 'ontological argument',
      'moral argument for god', 'kalam cosmological',
      'fine-tuned universe', 'fine tuned universe',
      'why gravity', 'did god create the universe', 'origin of the universe',
      'intelligent design', 'big bang and god',
      // Resurrection / Jesus
      'resurrection evidence', 'did jesus rise from the dead',
      'is jesus real', 'historical jesus', 'did jesus exist',
      // Comparative religion / false doctrine
      'purgatory is not biblical', 'why purgatory',
      'catholicism and the bible', 'roman catholic doctrine',
      'is catholicism biblical', 'works-based salvation',
      'disproving', 'refuting', 'rebutting',
      'false doctrine', 'false gospel', 'false teacher',
      'discerning false', 'counterfeit gospel',
      'wolves in sheep', 'wolves in sheeps clothing',
      // Logic / reason
      'solid arguments', 'astonishing arguments', 'strongest arguments',
      'miracles of jesus', 'did miracles happen', 'are miracles real',
      'case for christ', 'case for christianity',
      'problem of evil', 'can we know truth', 'truth claims',
      'logic and faith', 'faith and reason', 'rationality of faith',
      'relativism and christianity', 'objective morality',
      // Comparative worldviews
      'atheism and christianity', 'answering atheism', 'talking to atheists',
      'evolution and creation', 'science and faith',
      'islam and the gospel', 'allah vs god', 'muhammad',
      'jehovah witnesses', 'mormonism and the bible', 'is mormonism christian',
    ],
    words: [
      // Core apologetics vocabulary
      'apologetics', 'apologist', 'apologetic',
      'evidence', 'defend', 'defense', 'argument', 'proof',
      'refute', 'rebut', 'debunk', 'counter', 'disprove',
      'miracle', 'miracles', 'resurrection',
      'reliability', 'reliable', 'trustworthy',
      'logic', 'philosophy', 'intellectual', 'reason',
      'skeptic', 'skepticism', 'atheism', 'atheist', 'agnostic',
      'cosmology', 'universe', 'creation', 'creator',
      'heresy', 'heretical', 'orthodox', 'unorthodox',
      'doctrinal', 'doctrine',
      'purgatory', 'indulgences',
      'quran', 'muhammad', 'islam', 'buddhism', 'hinduism',
      'cults', 'mormonism', 'mormon',
      // Spanish equivalents
      'apologética', 'apologetica', 'evidencia', 'argumento',
      'resurrección', 'resurreccion', 'milagro', 'milagros',
      'ateísmo', 'ateismo', 'existencia de dios',
      'refutar', 'rebatir', 'falsa doctrina',
    ],
  },

  // ── Bible Study ────────────────────────────────────────────────────────────
  {
    id: 'bible-study',
    labelEn: 'Bible Study',
    labelEs: 'Estudio Bíblico',
    color: '#EF4444',
    boost: [
      'bible study', 'estudio bíblico', 'estudio biblico',
      'word of god', 'palabra de dios',
      'holy scripture', 'sacred scripture',
      'holy bible',
    ],
    phrases: [
      'what does the bible say', 'bible teaches', 'scripture teaches',
      'what does god say about', 'what god says about',
      'old testament', 'new testament', 'antiguo testamento', 'nuevo testamento',
      'book of genesis', 'book of psalms', 'book of proverbs', 'book of john',
      'book of romans', 'book of revelation', 'book of acts',
      'gospel of matthew', 'gospel of mark', 'gospel of luke', 'gospel of john',
      'verse by verse', 'chapter and verse', 'biblical truth', 'biblical teaching',
      'what scripture says', 'exegesis', 'hermeneutics', 'biblical interpretation',
      'sermon on the mount', 'beatitudes', "lord's prayer",
      'fruit of the spirit', 'armor of god', 'great commission',
      'ten commandments', 'laws of god',
      'upholding the truth of the bible', 'truth of the bible',
      'inerrancy of the bible', 'authority of the bible',
      // Spanish
      'qué dice la biblia', 'lo que dice dios', 'estudio de la biblia',
    ],
    words: [
      'bible', 'biblical', 'scripture', 'scriptures', 'verse', 'passage',
      'psalm', 'psalms', 'proverb', 'proverbs',
      'genesis', 'exodus', 'leviticus', 'numbers', 'deuteronomy',
      'joshua', 'judges', 'samuel', 'kings', 'chronicles',
      'ezra', 'nehemiah', 'esther', 'job', 'ecclesiastes', 'isaiah',
      'jeremiah', 'ezekiel', 'daniel', 'hosea', 'amos', 'jonah', 'micah',
      'matthew', 'mark', 'luke', 'acts', 'romans', 'corinthians',
      'galatians', 'ephesians', 'philippians', 'colossians', 'thessalonians',
      'timothy', 'titus', 'hebrews', 'james', 'revelation',
      'canon', 'covenant', 'prophecy', 'prophetic', 'prophet',
      'parable', 'sermon', 'theological', 'theology',
      'inerrancy', 'hermeneutic', 'exegesis',
      // Spanish
      'biblia', 'bíblica', 'biblica', 'escritura', 'escrituras',
      'versículo', 'versiculo', 'salmo', 'proverbio',
      'pacto', 'profecía', 'profecia', 'profeta',
      'teología', 'teologia',
    ],
  },

  // ── Daily Life ─────────────────────────────────────────────────────────────
  {
    id: 'daily-life',
    labelEn: 'Daily Life',
    labelEs: 'Vida Diaria',
    color: '#22C55E',
    boost: [
      'daily life', 'vida diaria', 'everyday life', 'vida cotidiana',
      'christian living', 'vida cristiana', 'practical faith',
      'navigating a secular workplace', 'secular workplace',
      'workplace as a christian', 'christian in the workplace',
      'toxic boss', 'toxic bosses', 'dealing with toxic',
    ],
    phrases: [
      // Workplace & society
      'navigating the secular', 'secular environment',
      'christian at work', 'faith at work', 'work and faith',
      'dealing with toxic', 'difficult coworkers', 'difficult boss',
      'when the light hurts', 'navigating darkness',
      // Relationships
      'christian marriage', 'christian family', 'christian parenting',
      'christian friendship', 'love your neighbor',
      'forgiveness in practice', 'forgiving others',
      'anger and the christian', 'dealing with anger',
      'how to deal with anger',
      // Personal habits
      'christian habits', 'daily habits', 'daily routine',
      'living as a christian', 'how christians should live',
      'how to be a better christian',
      // Mental health / emotions
      'anxiety and faith', 'worry and prayer', 'stress and god',
      'dealing with depression', 'loneliness and faith',
      // Finances
      'money and god', 'finances and faith', 'christian stewardship',
      // Spanish
      'vida del cristiano', 'cómo vivir como cristiano',
      'lugar de trabajo secular', 'jefe tóxico', 'jefes tóxicos',
      'navegar el mundo secular', 'manejar el enojo',
    ],
    words: [
      'daily', 'everyday', 'practical', 'lifestyle',
      'marriage', 'family', 'parenting', 'children', 'husband', 'wife',
      'workplace', 'coworker', 'colleague', 'boss', 'career', 'job',
      'friendship', 'relationship', 'community', 'neighbor',
      'habit', 'routine', 'discipline',
      'anxiety', 'worry', 'stress', 'anger', 'emotion',
      'loneliness', 'depression', 'mental',
      'money', 'finances', 'stewardship', 'generosity',
      // Spanish
      'diario', 'cotidiano', 'familia', 'matrimonio',
      'trabajo', 'carrera', 'compañero', 'jefe',
      'hábito', 'habito', 'rutina',
      'ansiedad', 'estrés', 'estres', 'enojo', 'ira',
    ],
  },

  // ── Lives that Speak ───────────────────────────────────────────────────────
  {
    id: 'lives-that-speak',
    labelEn: 'Lives that Speak',
    labelEs: 'Vidas que Hablan',
    color: '#EC4899',
    boost: [
      'lives that speak', 'vidas que hablan',
      'my testimony', 'my story', 'mi historia', 'mi testimonio',
      'my journey to faith', 'my journey with christ',
      'how i came to faith', 'how i found god', 'how i found jesus',
    ],
    phrases: [
      'how i became a christian', 'my conversion story', 'my conversion',
      'before i was a christian', 'i was not a christian',
      'how god changed my life', 'god transformed my life',
      'i found christ', 'i found jesus', 'i met jesus',
      'encounter with god', 'encounter with jesus',
      'god saved me from', 'born again story',
      'walking with god for', 'my walk with christ',
      'my faith journey', 'life of faith example',
      'christian biography', 'life of a believer',
      // Spanish
      'cómo llegué a la fe', 'cómo encontré a jesús', 'dios cambió mi vida',
    ],
    words: [
      'testimony', 'testimonies', 'testimonial',
      'conversion', 'converted', 'reborn',
      'encounter', 'experience', 'transformation', 'transformed',
      'healed', 'delivered', 'restored', 'renewed',
      'autobiograph', 'biography',
      // Spanish
      'testimonio', 'testimonios', 'conversión', 'conversion',
      'transformación', 'transformacion', 'renacimiento',
    ],
  },

  // ── News & Discernment ─────────────────────────────────────────────────────
  {
    id: 'news',
    labelEn: 'News & Discernment',
    labelEs: 'Noticias y Discernimiento',
    color: '#1E3A5F',
    boost: [
      'news and discernment', 'noticias y discernimiento',
      'discerning false teachers', 'discerning false doctrine',
      'false teachers in the church', 'false prophets today',
      'wolves in sheeps clothing', 'wolf in sheeps clothing',
      'beware of false', 'warning about',
      'prosperity gospel', 'health and wealth gospel',
      'progressive christianity', 'woke church',
    ],
    phrases: [
      // Church culture
      'state of the church', 'church in decline', 'church today',
      'decline of christianity', 'what is happening in christianity',
      'problems in the church', 'issues in the church',
      // False movements
      'word of faith movement', 'name it claim it',
      'new age movement', 'new age and christianity',
      'jehovah witnesses and the bible', 'mormonism explained',
      'christian science cult', 'scientology and christianity',
      // Socio-political
      'christian persecution', 'persecuted church', 'religious freedom',
      'cancel culture and christians', 'christians and politics',
      'social justice and the gospel',
      'gender ideology and the bible', 'critical race theory and the church',
      // Spanish
      'falsos maestros', 'falsa doctrina', 'discernimiento cristiano',
      'iglesia en declive', 'libertad religiosa',
    ],
    words: [
      'discernment', 'discerning',
      'persecution', 'persecuted',
      'freedom', 'rights', 'political', 'government',
      'warning', 'beware', 'alert', 'crisis',
      'denomination', 'movement', 'institution',
      // Spanish
      'discernimiento', 'advertencia', 'peligro', 'persecución',
      'persecucion', 'libertad', 'derechos',
    ],
  },

  // ── Spiritual Growth ───────────────────────────────────────────────────────
  {
    id: 'spiritual-growth',
    labelEn: 'Spiritual Growth',
    labelEs: 'Crecimiento Espiritual',
    color: '#4ADE80',
    boost: [
      'spiritual growth', 'crecimiento espiritual',
      'grow in faith', 'crecer en la fe',
      'discipleship', 'discipulado',
      'sanctification', 'santificación',
      'devotional', 'devotional life',
    ],
    phrases: [
      // Prayer
      'how to pray', 'the power of prayer', 'prayer life', 'life of prayer',
      'fasting and prayer', 'intercession', 'quiet time with god',
      // Scripture
      'how to read the bible', 'devotional reading', 'daily devotion',
      'drawing near to god', 'closer to god', 'knowing god more',
      // Holy Spirit
      'holy spirit', 'gifts of the spirit', 'filled with the spirit',
      'walking in the spirit', 'led by the spirit',
      // Salvation & gospel (teaching angle, not defending)
      'what is the gospel', 'plan of salvation', 'salvation explained',
      'repentance and faith', 'justification by faith', 'faith and works',
      'grace of god', 'mercy of god', 'love of god', 'god is love',
      // Eternal life
      'eternal life', 'heaven', 'what happens after death', 'kingdom of god',
      // Discipleship
      'following jesus', 'cost of discipleship', 'take up your cross',
      'abiding in christ', 'born of the spirit',
      'christian meditation', 'biblical meditation',
      'sabbath rest', 'worship and praise',
      // Spanish
      'cómo orar', 'vida de oración', 'tiempo con dios',
      'evangelio explicado', 'plan de salvación', 'gracia de dios',
    ],
    words: [
      'gospel', 'salvation', 'saved', 'grace', 'mercy',
      'prayer', 'pray', 'worship', 'praise',
      'faith', 'hope', 'trust', 'believe',
      'discipleship', 'disciple', 'sanctification',
      'repentance', 'repent', 'forgiveness', 'forgive',
      'righteousness', 'holiness', 'obedience',
      'evangelism', 'evangelist', 'preach', 'missions',
      'lord', 'savior', 'messiah', 'christ', 'jesus',
      // Spanish
      'evangelio', 'salvación', 'salvacion', 'gracia', 'misericordia',
      'oración', 'oracion', 'adoración', 'adoracion',
      'discipulado', 'santificación', 'santificacion',
      'arrepentimiento', 'perdón', 'perdon',
      'jesús', 'señor', 'salvador',
    ],
  },

  // ── Technology ─────────────────────────────────────────────────────────────
  {
    id: 'technology',
    labelEn: 'Technology',
    labelEs: 'Tecnología',
    color: '#14B8A6',
    boost: [
      'artificial intelligence', 'inteligencia artificial',
      'ai and faith', 'ai and christianity', 'ai and the bible',
      'technology and faith', 'tecnología y fe', 'tecnologia y fe',
      'machine learning', 'chatgpt', 'large language model', 'llm',
      'digital tools for christians', 'bible software',
      'christian technology', 'church technology',
    ],
    phrases: [
      'technology and the church', 'technology and christianity',
      'faith and technology', 'ai and god',
      'can ai understand the bible', 'artificial intelligence and the bible',
      'social media and the church', 'church online', 'online ministry',
      'digital evangelism', 'reaching people through technology',
      'bible apps', 'christian apps', 'software for christians',
      // Spanish
      'inteligencia artificial y la fe', 'tecnología en la iglesia',
      'herramientas digitales para cristianos',
    ],
    words: [
      // NOTE: these are whole-word matched so "toxic" won't trigger "tech"
      'technology', 'technological',
      'digital', 'software', 'platform', 'algorithm',
      'internet', 'online', 'streaming',
      'programming', 'developer',
      // Spanish
      'tecnología', 'tecnologia', 'plataforma', 'digital', 'inteligencia',
    ],
  },

  // ── Blog (catch-all — always last) ────────────────────────────────────────
  {
    id: 'blog',
    labelEn: 'Blog',
    labelEs: 'Blog',
    color: '#06B6D4',
    boost: [],
    phrases: [
      'i want to share', 'quiero compartir',
      'in this article', 'en este artículo',
      'my thoughts on', 'mis pensamientos sobre',
      'let me tell you', 'reflection on',
    ],
    words: [
      'blog', 'post', 'reflection', 'opinion',
      'reflexión', 'reflexion', 'compartir',
    ],
  },
];

// ── Scoring engine ─────────────────────────────────────────────────────────

/**
 * Builds a whole-word regex for a term.
 * e.g. "tech" → /\btech\b/i  → won't match "toxic" or "technical"
 */
function wholeWord(term: string): RegExp {
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`, 'i');
}

/** Score a single article against all categories. Returns sorted array. */
export function scoreArticle(
  title: string,
  subtitle: string
): Array<{ id: string; score: number }> {
  const haystack = `${title} ${subtitle}`.toLowerCase();

  return CATEGORIES.map((cat) => {
    let score = 0;

    // Boost exact phrases → 5 pts each (substring match is fine here — phrases are specific)
    for (const b of cat.boost) {
      if (haystack.includes(b.toLowerCase())) score += 5;
    }

    // Multi-word phrases → 3 pts each
    for (const p of cat.phrases) {
      if (haystack.includes(p.toLowerCase())) score += 3;
    }

    // Single words → 1 pt each, using word-boundary regex to avoid false substring hits
    for (const w of cat.words) {
      if (wholeWord(w).test(haystack)) score += 1;
    }

    return { id: cat.id, score };
  }).sort((a, b) => b.score - a.score);
}

/**
 * Infers up to 2 categories for an article using the weighted scoring engine.
 * - Primary:   highest-scoring category
 * - Secondary: included if it scores ≥ 3 AND within 60% of winner's score
 * - Fallback:  'blog' when no category scores > 0
 */
export function inferCategories(title: string, subtitle: string): string[] {
  const ranked = scoreArticle(title, subtitle);
  const top = ranked[0];
  if (!top || top.score === 0) return ['blog'];

  const results: string[] = [top.id];
  const second = ranked[1];
  if (second && second.score >= 3 && second.score >= top.score * 0.6) {
    results.push(second.id);
  }
  return results;
}
